import React, { useEffect, useState } from 'react';
import { Button, Drawer, Flex, Loader, Text } from '@mantine/core';
import { useLazyQuery, useMutation } from '@apollo/client';

import { Notification } from '../../codegen/graphql';
import NotificationItem from './NotificationItem';
import {
    DELETE_ALL_NOTIFICATIONS,
    GET_NOTIFICATIONS,
    GET_UNREAD_NOTIFICATION_COUNT,
} from '../../apollo/mutations/notification';

type Props = {
    opened: boolean;
    close: () => void;
};

function NotificationDrawer({ opened, close }: Props) {
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const [fetchNotifications, { loading }] = useLazyQuery(GET_NOTIFICATIONS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            setNotifications(data.getNotifications);
        },
    });

    useEffect(() => {
        if (opened) {
            fetchNotifications();
        }
    }, [opened, fetchNotifications]);

    const [deleteAllNotifications] = useMutation(DELETE_ALL_NOTIFICATIONS, {
        refetchQueries: [GET_NOTIFICATIONS, GET_UNREAD_NOTIFICATION_COUNT],
    });

    const removeNotification = (id: string) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

    const removeAllNotifications = () => {
        setNotifications([]);
    };

    const onClickClearAll = () => {
        removeAllNotifications();
        deleteAllNotifications();
    };

    return (
        <Drawer
            title={
                <Flex w="100%" justify="space-between">
                    <Text className="text-primary font-semibold text-lg">
                        Notifications
                    </Text>
                    {notifications.length > 0 && (
                        <Button
                            fullWidth={false}
                            variant="light"
                            size="compact-xs"
                            color="red"
                            onClick={onClickClearAll}
                        >
                            Clear All
                        </Button>
                    )}
                </Flex>
            }
            classNames={{
                title: 'w-full',
                body: 'p-0',
            }}
            opened={opened}
            onClose={close}
            closeButtonProps={{
                display: 'none',
            }}
            size="lg"
        >
            <div>
                {loading && notifications.length === 0 && (
                    <Flex justify="center" py="xl">
                        <Loader size="sm" />
                    </Flex>
                )}
                {notifications.length > 0 &&
                    notifications.map((notification: Notification) => (
                        <NotificationItem
                            key={notification.id}
                            notification={notification}
                            close={close}
                            removeNotification={removeNotification}
                        />
                    ))}
                {!loading && notifications.length === 0 && (
                    <p className="text-center text-gray-500">
                        No notifications
                    </p>
                )}
            </div>
        </Drawer>
    );
}

export default NotificationDrawer;
